/** Buy Free Games button — gold frame over green rect with text */

import { Container, Graphics, Text, TextStyle, Sprite, Texture, Assets } from 'pixi.js';
import { Logger } from '../utils/Logger';

const logger = new Logger('BuyFeatureButton');

/** Cost multiplier for buying free games */
export const BUY_FEATURE_COST_MULTIPLIER = 80;

const FRAME_ASSET = 'assets/ui/frame_buttion_baf.png';

/** Interactive "BUY FEATURE" button using gold frame asset */
export class BuyFeatureButton extends Container {
  private _enabled: boolean = true;
  private _fallbackBg: Graphics;
  private _frameSprite: Sprite | null = null;
  private _buyLabel: Text;
  private _featureLabel: Text;
  private _costLabel: Text;
  private _onBuy: (() => void) | null = null;
  private _totalBet: number = 0;
  private _useFallback: boolean = true;

  constructor() {
    super();
    this.eventMode = 'static';
    this.cursor = 'pointer';

    // Fallback green rectangle background (shown until frame asset loads)
    this._fallbackBg = new Graphics();
    this._drawFallbackBg(0x00aa00);
    this.addChild(this._fallbackBg);

    // "BUY" text
    this._buyLabel = new Text({
      text: 'BUY',
      style: new TextStyle({
        fontFamily: 'Arial Black, Arial',
        fontSize: 40,
        fill: 0xffffff,
        fontWeight: 'bold',
        stroke: { color: 0x000000, width: 3 },
      }),
    });
    this._buyLabel.anchor.set(0.5, 1);
    this._buyLabel.y = -8;
    this.addChild(this._buyLabel);

    // "FEATURE" text
    this._featureLabel = new Text({
      text: 'FEATURE',
      style: new TextStyle({
        fontFamily: 'Arial Black, Arial',
        fontSize: 32,
        fill: 0xffffff,
        fontWeight: 'bold',
        stroke: { color: 0x000000, width: 3 },
      }),
    });
    this._featureLabel.anchor.set(0.5, 0);
    this._featureLabel.y = -6;
    this.addChild(this._featureLabel);

    // Cost text
    this._costLabel = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: 'Arial, Helvetica',
        fontSize: 24,
        fill: 0xffdd00,
        align: 'center',
        stroke: { color: 0x000000, width: 2 },
      }),
    });
    this._costLabel.anchor.set(0.5, 0);
    this._costLabel.y = 30;
    this.addChild(this._costLabel);

    // Load the gold frame sprite (the actual button background)
    this._loadFrame();

    // Pointer events
    this.on('pointerdown', this._handlePress, this);
    this.on('pointerup', this._handleRelease, this);
    this.on('pointerupoutside', this._handleRelease, this);
    this.on('pointerover', this._handleOver, this);
    this.on('pointerout', this._handleOut, this);
  }

  /** Register the buy callback */
  onBuy(callback: () => void): void {
    this._onBuy = callback;
  }

  /** Update the displayed cost based on current total bet */
  updateCost(totalBet: number): void {
    this._totalBet = totalBet;
    const cost = totalBet * BUY_FEATURE_COST_MULTIPLIER;
    this._costLabel.text = `$${cost.toFixed(2)}`;
  }

  /** Get the cost for the current bet level */
  getCost(): number {
    return this._totalBet * BUY_FEATURE_COST_MULTIPLIER;
  }

  /** Enable/disable the button */
  setEnabled(enabled: boolean): void {
    this._enabled = enabled;
    this.cursor = enabled ? 'pointer' : 'default';
    this.alpha = enabled ? 1.0 : 0.4;
    if (this._useFallback) {
      this._drawFallbackBg(enabled ? 0x00aa00 : 0x444444);
    }
  }

  private _drawFallbackBg(color: number): void {
    this._fallbackBg.clear();
    const w = 391;
    const h = 171;
    this._fallbackBg.roundRect(-w / 2, -h / 2, w, h, 12);
    this._fallbackBg.fill({ color, alpha: 1.0 });
  }

  private async _loadFrame(): Promise<void> {
    try {
      const tex = await Assets.load<Texture>(FRAME_ASSET).catch(() => null);
      if (tex) {
        this._frameSprite = new Sprite(tex);
        this._frameSprite.anchor.set(0.5);
        // Insert frame as the background layer (index 0, below text)
        this.addChildAt(this._frameSprite, 0);

        // Remove the fallback green rectangle — the frame IS the button bg
        this.removeChild(this._fallbackBg);
        this._fallbackBg.destroy();
        this._useFallback = false;

        logger.info('Buy feature frame sprite loaded — using as button background');
      }
    } catch (err) {
      logger.warn(`Failed to load buy feature frame: ${err}`);
    }
  }

  private _handlePress(): void {
    if (!this._enabled) return;
    if (this._useFallback) {
      this._drawFallbackBg(0x008800);
    } else if (this._frameSprite) {
      this._frameSprite.tint = 0xcccccc;
    }
    this.scale.set(0.95);
  }

  private _handleRelease(): void {
    if (!this._enabled) return;
    if (this._useFallback) {
      this._drawFallbackBg(0x00aa00);
    } else if (this._frameSprite) {
      this._frameSprite.tint = 0xffffff;
    }
    this.scale.set(1.0);

    logger.info('Buy Feature button pressed');
    if (this._onBuy) {
      this._onBuy();
    }
  }

  private _handleOver(): void {
    if (!this._enabled) return;
    if (this._useFallback) {
      this._drawFallbackBg(0x00cc00);
    } else if (this._frameSprite) {
      this._frameSprite.tint = 0xeeffee;
    }
  }

  private _handleOut(): void {
    if (!this._enabled) return;
    if (this._useFallback) {
      this._drawFallbackBg(0x00aa00);
    } else if (this._frameSprite) {
      this._frameSprite.tint = 0xffffff;
    }
    this.scale.set(1.0);
  }
}
